import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { Card } from '@pesto/ui/components/Card';
import { Typography } from '@pesto/ui/components/Typography';
import { SmartForm } from '@pesto/ui/features/SmartForm/SmartForm';
import { useState } from 'react';
import { z } from 'zod';

import type { CardModalPayload, ModalProps } from '../types/modal.types';

import { useGetCardStatusLazyQuery, useGetUserQuery } from '../../__generated__/graphql/api';

const schema = z.object({
  referenceId: z.string().min(1, 'Reference ID is required'),
});

export const GetCardStatusComponent = ({ payload }: ModalProps<CardModalPayload>) => {
  const { userId = '', cardReferenceId } = payload;

  const { data } = useGetUserQuery({
    variables: {
      userId,
    },
    skip: !userId || !!cardReferenceId,
  });

  const { handleMutation } = useMutationWithToast();

  const [getCardStatus, { loading }] = useGetCardStatusLazyQuery({
    fetchPolicy: 'network-only',
  });

  const [status, setStatus] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const handleSubmit = (data: z.infer<typeof schema>) => {
    setStatus(null);
    setStatusError(null);
    void handleMutation({
      mutation: () =>
        getCardStatus({
          variables: {
            referenceId: data.referenceId,
          },
        }),
      extractSuccess: (result) => !!result?.data,
      onSuccess: (result) => {
        const errorMessage = result?.errors?.[0]?.message;

        if (errorMessage) {
          setStatusError(errorMessage);

          return;
        }

        const rawStatus = result?.data?.getCardStatus;
        const statusValue = rawStatus && rawStatus.trim() ? rawStatus : 'No status returned';
        setStatus(statusValue);
      },
      onError: (error) => {
        setStatusError(error.message || 'Failed to get card status');
      },
      debugLabel: 'get-card-status',
      toast: {
        disabled: true,
      },
    });
  };

  const user = data?.user;
  const activeCard = user?.cards?.find((card) => card?.active);
  const referenceId = cardReferenceId ?? activeCard?.referenceId ?? '';

  const config: SmartFormConfig<z.infer<typeof schema>> = {
    fields: [
      cardReferenceId
        ? {
            name: 'referenceId',
            label: '',
            type: 'text',
            defaultValue: referenceId,
            componentProps: {
              wrapperClassName: 'hidden',
              inputClassName: 'hidden',
            },
          }
        : {
            name: 'referenceId',
            label: 'Card Reference ID',
            type: 'select',
            required: true,
            options:
              user?.cards?.map((card) => ({
                value: card?.referenceId ?? '',
                label: card?.referenceId ?? '',
              })) ?? [],
          },
    ],
    schema,
    defaultValues: {
      referenceId,
    },
  };

  return (
    <div className="flex flex-col gap-4">
      <SmartForm config={config} onSubmit={handleSubmit} loading={loading} />
      {statusError && (
        <Card className="border-destructive/30 bg-destructive/5 p-3">
          <Typography variant="bodySmall" className="text-destructive text-sm">
            {statusError}
          </Typography>
        </Card>
      )}
      {status && (
        <Card className="bg-muted/50 p-3">
          <Typography variant="bodySmall" className="text-muted-foreground text-xs font-semibold uppercase">
            Card Status
          </Typography>
          <Typography variant="body" className="font-mono text-sm">
            {status}
          </Typography>
        </Card>
      )}
    </div>
  );
};
